import React, { useEffect, useState } from "react";
import { useWallet } from "../hooks/useWallet";
import { AlertCircle, Wallet, ArrowUpRight, ArrowDownLeft } from "lucide-react";

function StatusBadge({ status }) {
  const styles = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
    inactive: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
    blocked: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
    pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
    failed: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${styles[status] || styles.inactive}`}>
      {status}
    </span>
  );
}

export default function AdminMarketplaceWallet() {
  const { wallets, transfers, transactions, summary, loading, error, fetchWallets, fetchSummary, fetchTransfers, fetchMyTransactions } = useWallet();
  const [activeTab, setActiveTab] = useState("wallets");

  useEffect(() => {
    fetchWallets();
    fetchSummary();
  }, [fetchWallets, fetchSummary]);

  useEffect(() => {
    if (activeTab === "transfers") fetchTransfers();
    if (activeTab === "transactions") fetchMyTransactions();
  }, [activeTab, fetchTransfers, fetchMyTransactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin dark:border-indigo-500/20 dark:border-t-indigo-400" />
          <p className="text-sm text-slate-500 dark:text-slate-500">Loading wallet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center dark:bg-rose-500/10 dark:border-rose-500/30">
        <AlertCircle size={32} className="mx-auto text-rose-500 mb-2 dark:text-rose-400" />
        <p className="text-rose-700 font-medium dark:text-rose-400">Failed to load wallet data</p>
        <button onClick={() => fetchWallets()} className="mt-3 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Wallet</h1>
        <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-500">View your wallet balance and transaction history.</p>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <div className="flex items-center gap-2 mb-1">
              <Wallet size={18} className="text-indigo-600 dark:text-indigo-400" />
              <p className="text-sm text-slate-500 dark:text-slate-500">Total Balance</p>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total_balance} MAD</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Active Wallets</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.active_wallets}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Total Wallets</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.wallet_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Currencies</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{Object.keys(summary.by_currency || {}).join(", ") || "MAD"}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { key: "wallets", label: "Wallets" },
          { key: "transfers", label: "Transfers" },
          { key: "transactions", label: "My Transactions" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab.key
                ? "border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Wallets Tab */}
      {activeTab === "wallets" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
          {wallets.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500 text-sm dark:text-slate-500">No wallets found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-800/60 dark:border-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Wallet #</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Balance</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Owner</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {wallets.map((w) => (
                    <tr key={w.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-4 py-3 font-medium dark:text-slate-100">{w.name}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs dark:text-slate-400">{w.num_wallet}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-600 dark:text-emerald-400">{w.solde} {w.currency}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-500">{w.owner_type}</td>
                      <td className="px-4 py-3"><StatusBadge status={w.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transfers Tab */}
      {activeTab === "transfers" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
          {transfers.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500 text-sm dark:text-slate-500">No transfers found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-800/60 dark:border-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">From</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">To</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {transfers.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-4 py-3 text-xs font-mono dark:text-slate-400">{t.sender_wallet_number}</td>
                      <td className="px-4 py-3 text-xs font-mono dark:text-slate-400">{t.receiver_wallet_number}</td>
                      <td className="px-4 py-3 font-semibold dark:text-slate-100">{t.amount} {t.currency}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize dark:text-slate-400">{t.transaction_type}</td>
                      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-3 text-slate-500 text-xs dark:text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === "transactions" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500 text-sm dark:text-slate-500">No transactions found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-800/60 dark:border-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {transactions.map((t, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {t.direction === "CREDIT" ? (
                            <ArrowDownLeft size={16} className="text-emerald-600 dark:text-emerald-400" />
                          ) : (
                            <ArrowUpRight size={16} className="text-rose-600 dark:text-rose-400" />
                          )}
                          <span className="capitalize dark:text-slate-300">{t.transaction_type}</span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-semibold ${t.direction === "CREDIT" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                        {t.direction === "CREDIT" ? "+" : "-"}{t.amount} {t.currency}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-3 text-slate-500 text-xs dark:text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
